import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { PRESET_FLOW_HANDLER, PRESET_FLOW_QUERY_PARAM } from '../../features/onboarding';

import { BLOCKING_REQUEST, isOutageStatus } from '../error/blocking-request';
import { ApiError, ForbiddenError, NotFoundError, UnauthorizedError } from '../error/dxp-error';
import { ENVIRONMENT, PORTAL_AVAILABILITY } from '../tokens/app.tokens';

// Endpoint che il flusso Cittadino anonimo chiama prima che una sessione BFF esista: un 401 su questi
// e' l'esito atteso di un percorso anonimo, non un fallimento di autenticazione da rimandare al login
// CIAM. '/bff/user' e' la sonda di sessione; '/tenant/access-config' porta le decisioni del portale, che
// si leggono prima di entrare.
const ANONYMOUS_PATHS: readonly string[] = [
  '/bff/user',
  '/tenant/access-config',
  '/tenant/resolution',
];

/**
 * Le aree del cittadino. Un 401 qui non e' «non sei chi dici», e' «la tua visita e' scaduta»: il
 * biglietto anonimo scorre e dopo un'inattivita' si chiude, e chi torna ricomincia dalla scelta della
 * lingua — mandarlo alla pagina di Entra gli chiederebbe credenziali che non ha.
 *
 * Il criterio e' l'indirizzo e non il ruolo perche' il ruolo si legge da `GET /bff/user`, che in questo
 * momento e' proprio la chiamata che sta rispondendo 401. Una postazione operatore che si trovasse qui —
 * apre la stessa pagina della chat — finirebbe sulla scelta della lingua invece che sul proprio accesso:
 * e' un giro in piu', non una porta chiusa, e l'intestazione le offre comunque la propria.
 */
const CITIZEN_AREAS: readonly string[] = ['/onboarding', '/chat'];

/**
 * Il motivo con cui il BFF rifiuta un operatore che non e' membro del gruppo del dominio da cui accede.
 * Lo stesso 403 copre altre operazioni negate, e solo questo chiude la postazione intera.
 */
const OPERATOR_NOT_ENABLED_REASON = 'operator-not-enabled';

/**
 * Dove ricomincia una visita anonima scaduta: la scelta della lingua dello sportello del QR inquadrato,
 * che apre solo il flow scritto nell'indirizzo (#1769). Senza un QR, l'onboarding manda all'invito a
 * inquadrarne uno. Il flow e' un identificativo gia' passato dalla sua forma, senza niente da codificare.
 */
function citizenEntryUrl(rememberedFlow: string | null): string {
  return rememberedFlow
    ? `/onboarding/language?${PRESET_FLOW_QUERY_PARAM}=${rememberedFlow}`
    : '/onboarding';
}

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const env = inject(ENVIRONMENT);
  const router = inject(Router);
  const availability = inject(PORTAL_AVAILABILITY);
  // Il flow si legge solo al 401 di un cittadino, e l'injector e' quello che resta dopo il contesto
  // d'iniezione: dentro `catchError` non si puo' piu' chiamare `inject`.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Prima della traduzione e a prescindere da chi la raccoglie: chi fa una richiesta bloccante di
      // solito ne inghiotte l'errore — il bottone si riabilita, la sonda risponde «anonimo» — ed e'
      // proprio per questo che l'indisponibilita' si alza qui e non la' dove l'errore arriva.
      if (req.context.get(BLOCKING_REQUEST) && isOutageStatus(err.status)) {
        availability.reportOutage();
      }

      switch (err.status) {
        case 401:
          if (!ANONYMOUS_PATHS.some((path) => req.url.endsWith(path))) {
            if (isCitizenArea(router.url)) {
              void router.navigateByUrl(
                citizenEntryUrl(injector.get(PRESET_FLOW_HANDLER).remembered()),
              );
            } else window.location.href = loginUrl(env.apiUrl);
          }
          return throwError(() => new UnauthorizedError());
        case 403:
          if (reasonOf(err.error) === OPERATOR_NOT_ENABLED_REASON)
            availability.reportOperatorNotEnabled();
          return throwError(() => new ForbiddenError());
        case 404:
          return throwError(() => new NotFoundError(req.url));
        default:
          return throwError(() => new ApiError(err.status, err.message, reasonOf(err.error)));
      }
    }),
  );
};

/**
 * L'accesso porta indietro dove la persona stava: il BFF puo' essere servito da un host suo, e senza
 * questo indirizzo finito il login si ritroverebbe sulla radice di quell'host invece che nel portale.
 * L'indirizzo lo costruisce anche `HttpAuthProvider`, che e' il posto da cui lo prendono intestazione e
 * guard; qui si ripete perche' un interceptor non puo' iniettare cio' che a sua volta usa `HttpClient`.
 */
function loginUrl(apiUrl: string): string {
  const parameters = new URLSearchParams({ returnUrl: window.location.href });

  return `${apiUrl}/bff/login?${parameters.toString()}`;
}

/** Il `reason` del ProblemDetails del BFF, quando c'e' ed e' una parola. */
function reasonOf(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;

  const reason = (body as { reason?: unknown }).reason;
  return typeof reason === 'string' && reason.length > 0 ? reason : null;
}

function isCitizenArea(url: string): boolean {
  return CITIZEN_AREAS.some((area) => url === area || url.startsWith(`${area}/`));
}
